import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import StatusMessage from '../components/StatusMessage';
import ConfirmDialog from '../components/ConfirmDialog';


interface Keynote {
  id: string;
  title: string;
  description: string;
  speaker: string;
  duration: string;
  topic: string;
  targetAudience: string;
  street: string;
  houseNumber: string;
  zip: string;
  city: string;
  date: string;
  time: string;
  price: string;
  maxAttendees: number;
  currentAttendees: number;
  imageUrl?: string;
  speakerBio?: string;
  objectives?: string;
  prerequisites?: string;
  url?: string;
  isActive: boolean;

  createdAt: string;
  updatedAt: string;
}

type SortField = 'title' | 'speaker' | 'date' | 'price' | 'maxAttendees' | 'isActive';
type SortDirection = 'asc' | 'desc';

interface AdminKeynotesOverviewProps {
  onEditKeynote?: (keynoteId: string) => void;
}

const AdminKeynotesOverview: React.FC<AdminKeynotesOverviewProps> = ({ onEditKeynote }) => {
  const [keynotes, setKeynotes] = useState<Keynote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ isOpen: boolean; keynoteId: string; keynoteTitle: string }>({ isOpen: false, keynoteId: '', keynoteTitle: '' });
  const [sortField, setSortField] = useState<SortField>('title');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const formatAddress = (street: string, houseNumber: string, zip: string, city: string) => {
    const addressParts = [];
    
    // Street and house number
    if (street && houseNumber) {
      addressParts.push(`${street} ${houseNumber}`);
    } else if (street) {
      addressParts.push(street);
    }
    
    // Zip and city
    if (zip && city) {
      addressParts.push(`${zip} ${city}`);
    } else if (city) {
      addressParts.push(city);
    }
    
    return addressParts.length > 0 ? addressParts.join(', ') : 'TBD';
  };

  useEffect(() => {
    fetchKeynotes();
  }, []);

  const fetchKeynotes = async () => {
    try {
      setLoading(true);
      const client = generateClient<Schema>({
        authMode: 'userPool'
      });

      // Fetch ALL keynotes with pagination - Amplify handles environment isolation automatically
      let allKeynotes: any[] = [];
      let nextToken: string | null | undefined = null;
      let hasErrors = false;
      let errorMessages: any[] = [];
      
      do {
        const response: any = await client.models.Keynote.list({
          limit: 1000,
          nextToken: nextToken as any,
          selectionSet: [
            'id',
            'title',
            'description',
            'speaker',
            'duration',
            'topic',
            'targetAudience',
            'street',
            'houseNumber',
            'zip',
            'city',
            'date',
            'time',
            'price',
            'maxAttendees',
            'currentAttendees',
            'imageUrl',
            'speakerBio',
            'objectives',
            'prerequisites',
            'url',
            'isActive',
            'createdAt',
            'updatedAt'
          ]
        });
        
        if (response.errors && response.errors.length > 0) {
          hasErrors = true;
          errorMessages = [...errorMessages, ...response.errors];
        }
        
        allKeynotes = [...allKeynotes, ...(response.data || [])];
        nextToken = response.nextToken;
        
        console.log(`📊 AdminKeynotes: Fetched ${response.data?.length || 0} keynotes (total: ${allKeynotes.length})`);
      } while (nextToken);
      
      console.log(`✅ AdminKeynotes: Total keynotes fetched: ${allKeynotes.length}`);

      if (hasErrors) {
        console.error('🔴 Keynote query errors:', JSON.stringify(errorMessages, null, 2));
        setError('Failed to load keynotes');
      } else {
        setKeynotes(allKeynotes as Keynote[]);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching keynotes:', err);
      setError('Failed to load keynotes');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (keynote: Keynote) => {
    try {
      const client = generateClient<Schema>({ authMode: 'userPool' });
      await client.models.Keynote.update({
        id: keynote.id,
        isActive: !keynote.isActive
      });
      await fetchKeynotes();
    } catch (err) {
      console.error('Error updating keynote:', err);
      setMessage({ type: 'error', text: 'Failed to update keynote status' });
    }
  };

  const handleDeleteKeynote = (keynoteId: string) => {
    const keynote = keynotes.find(k => k.id === keynoteId);
    if (keynote) {
      setConfirmDialog({
        isOpen: true,
        keynoteId,
        keynoteTitle: keynote.title
      });
    }
  };

  const confirmDeleteKeynote = async () => {
    const { keynoteId } = confirmDialog;
    setConfirmDialog({ isOpen: false, keynoteId: '', keynoteTitle: '' });

    try {
      const client = generateClient<Schema>({ authMode: 'userPool' });
      
      // SOFT DELETE: Set status to 'deleted' and add deletedAt timestamp
      // This preserves data immutability - never hard delete user data
      await client.models.Keynote.update({ 
        id: keynoteId,
        status: 'deleted',
        deletedAt: new Date().toISOString(),
        deletedBy: 'admin' // TODO: Get actual admin user email
      });
      
      await fetchKeynotes();
      setMessage({ type: 'success', text: 'Keynote moved to deleted items!' });
    } catch (err) {
      console.error('Error deleting keynote:', err);
      setMessage({ type: 'error', text: 'Failed to delete keynote' });
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter keynotes into active and inactive
  const activeKeynotes = keynotes.filter(keynote => keynote.isActive);
  const inactiveKeynotes = keynotes.filter(keynote => !keynote.isActive);

  const sortKeynotes = (keynotesToSort: Keynote[]) => {
    return [...keynotesToSort].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle price sorting (remove € and convert to number)
      if (sortField === 'price') {
        aValue = parseFloat(aValue.replace(/[€,]/g, '')) || 0;
        bValue = parseFloat(bValue.replace(/[€,]/g, '')) || 0;
      }

      // Handle date sorting
      if (sortField === 'date') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Handle number sorting
      if (sortField === 'maxAttendees') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      }

      // Handle boolean sorting (isActive)
      if (sortField === 'isActive') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedActiveKeynotes = sortKeynotes(activeKeynotes);
  const sortedInactiveKeynotes = sortKeynotes(inactiveKeynotes);

  const renderTable = (keynotesToRender: Keynote[], title: string, isActive: boolean) => (
    <div className="admin-content-card" style={{ padding: 0, marginBottom: '24px' }}>
      {keynotesToRender.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
          <p>No {title.toLowerCase()} found.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', margin: 0, padding: 0 }}>
          <table className="admin-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                  {isActive ? 'Active Keynotes' : 'Deactivated Keynotes'} {sortField === 'title' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('speaker')} style={{ cursor: 'pointer' }}>
                  Speaker {sortField === 'speaker' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>
                  Price {sortField === 'price' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('maxAttendees')} style={{ cursor: 'pointer' }}>
                  Capacity {sortField === 'maxAttendees' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Attendees</th>
                <th onClick={() => handleSort('isActive')} style={{ cursor: 'pointer' }}>
                  Status {sortField === 'isActive' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {keynotesToRender.map((keynote) => (
                <tr key={keynote.id}>
                  <td>
                    <div style={{ fontWeight: 600, color: '#1f2937' }}>
                      {keynote.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      {keynote.topic}
                    </div>
                  </td>
                  <td style={{ fontWeight: 600 }}>{keynote.speaker}</td>
                  <td>
                    <div>{keynote.date || '-'}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{keynote.time || '-'}</div>
                  </td>
                  <td style={{ fontWeight: 600 }}>€{keynote.price}</td>
                  <td>{keynote.maxAttendees}</td>
                  <td>
                    <span style={{ 
                      color: keynote.currentAttendees >= keynote.maxAttendees ? '#dc2626' : '#16a34a',
                      fontWeight: 600 
                    }}>
                      {keynote.currentAttendees}/{keynote.maxAttendees}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${keynote.isActive ? 'active' : 'inactive'}`}>
                      {keynote.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      {onEditKeynote && (
                        <button
                          onClick={() => onEditKeynote(keynote.id)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #8B5A2B',
                            borderRadius: '6px',
                            background: 'white',
                            color: '#8B5A2B',
                            fontSize: '12px',
                            cursor: 'pointer',
                            marginRight: '8px'
                          }}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleActive(keynote)}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #008F99',
                          borderRadius: '6px',
                          background: 'white',
                          color: '#008F99',
                          fontSize: '12px',
                          cursor: 'pointer',
                          marginRight: '8px'
                        }}
                      >
                        {keynote.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => {
                          if (keynote.url) {
                            window.location.href = keynote.url;
                          } else {
                            window.location.href = '/ki-impulse';
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #008F99',
                          borderRadius: '6px',
                          background: 'white',
                          color: '#008F99',
                          fontSize: '12px',
                          cursor: 'pointer',
                          marginRight: '8px'
                        }}
                        title={keynote.url ? `Visit ${keynote.url}` : 'Visit keynotes overview'}
                      >
                        Visit
                      </button>
                      <button
                        onClick={() => handleDeleteKeynote(keynote.id)}
                        className="danger"
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #dc2626',
                          borderRadius: '6px',
                          background: 'white',
                          color: '#dc2626',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Loading keynotes...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Error: {error}</h2>
          <button className="admin-btn primary" onClick={fetchKeynotes}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '60px', minHeight: 'calc(100vh - 77px)', display: 'flex', flexDirection: 'column' }}>
      {message && (
        <div style={{ marginBottom: '16px' }}>
          <StatusMessage
            type={message.type}
            message={message.text}
            onClose={() => setMessage(null)}
          />
        </div>
      )}
      
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Keynote"
        message={`Are you sure you want to delete the keynote "${confirmDialog.keynoteTitle}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDeleteKeynote}
        onCancel={() => setConfirmDialog({ isOpen: false, keynoteId: '', keynoteTitle: '' })}
      />
      
      {renderTable(sortedActiveKeynotes, 'Active Keynotes', true)}
      {renderTable(sortedInactiveKeynotes, 'Deactivated Keynotes', false)}
      
      {/* Blank footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '280px',
        right: 0,
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.06)',
        zIndex: 200,
        padding: '12px 24px',
        height: '48px'
      }} />
    </div>
  );
};

export default AdminKeynotesOverview;
